import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderArchive, Download, Printer, Search } from 'lucide-react';
import { useMechanicAuth } from '../../mechanic/MechanicAuthContext';
import { AuditTable, TYPE_META } from '../components/compliance/AuditTable';
import { RetentionCard } from '../components/compliance/RetentionCard';
import { RevisionsDialog } from '../components/compliance/RevisionsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const yearsAgo = (n) => { const d = new Date(); d.setFullYear(d.getFullYear() - n); return d.toISOString().slice(0, 10); };
const PRESETS = [
  { key: '1y', label: '12 derniers mois', from: () => yearsAgo(1) },
  { key: '2y', label: '2 dernières années (SAAQ)', from: () => yearsAgo(2) },
  { key: 'ytd', label: 'Année en cours', from: () => `${new Date().getFullYear()}-01-01` },
  { key: 'all', label: 'Tout', from: () => '' },
];
const inputCls = 'bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white';

export default function ComplianceAuditPage() {
  const { token } = useMechanicAuth();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [owner, setOwner] = useState('own');
  const [from, setFrom] = useState(yearsAgo(2));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [versionsOf, setVersionsOf] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const params = { vehicle_id: vehicleId || undefined, owner, date_from: from || undefined, date_to: to || undefined };
  const owners = [...new Map(vehicles.map(v => [v.owner_key, v.owner_label])).entries()];
  const ownerVehicles = vehicles.filter(v => v.owner_key === owner);

  useEffect(() => {
    axios.get(`${API_URL}/api/compliance/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setVehicles(r.data || [])).catch(() => {});
  }, [token]);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/compliance/audit`, { headers: { Authorization: `Bearer ${token}` }, params: { vehicle_id: vehicleId || undefined, owner, date_from: from || undefined, date_to: to || undefined } });
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de chargement'); }
    finally { setLoading(false); }
  }, [token, vehicleId, owner, from, to]);

  useEffect(() => { search(); }, [owner, vehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API_URL}/api/compliance/audit/export`, { headers, params, responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'Dossier_audit_SAAQ.zip';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success('Dossier d\u2019audit téléchargé');
    } catch { toast.error('Erreur lors de la génération du dossier'); }
    finally { setDownloading(false); }
  };

  const ownerLabel = owners.find(([k]) => k === owner)?.[1] || 'Ma flotte';
  const vLabel = `${ownerLabel} — ${vehicleId ? (vehicles.find(v => v.id === vehicleId)?.unit_number || '') : 'tous les véhicules'}`;

  return (
    <div className="p-4 space-y-4" data-testid="compliance-audit-page">
      <div>
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><FolderArchive size={20} className="text-amber-400" /> Dossier d'audit SAAQ</h1>
        <p className="text-xs text-slate-400 mt-1">Toutes les preuves d'inspection, de mesures freins/pneus, de réparation et d'entretien pour une période donnée.</p>
      </div>

      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-3 space-y-3 print:hidden" data-testid="audit-filters">
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs text-slate-400 flex flex-col gap-1">Dossier de
            <select value={owner} onChange={e => { setOwner(e.target.value); setVehicleId(''); setData(null); }} className={inputCls} data-testid="audit-owner-select">
              {(owners.length ? owners : [['own', 'Ma flotte']]).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-400 flex flex-col gap-1">Véhicule
            <select value={vehicleId} onChange={e => setVehicleId(e.target.value)} className={inputCls} data-testid="audit-vehicle-select">
              <option value="">Tous les véhicules</option>
              {ownerVehicles.map(v => <option key={v.id} value={v.id}>{`#${v.unit_number} ${v.plate_number || ''}${v.is_active === false ? ' (retiré)' : ''}`}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-400 flex flex-col gap-1">Du
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputCls} data-testid="audit-date-from" />
          </label>
          <label className="text-xs text-slate-400 flex flex-col gap-1">Au
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputCls} data-testid="audit-date-to" />
          </label>
          <button onClick={search} disabled={loading} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 text-black text-sm font-semibold disabled:opacity-50" data-testid="audit-search-btn">
            <Search size={14} /> {loading ? 'Recherche…' : 'Afficher'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => { setFrom(p.from()); setTo(today()); }}
              className="px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 text-xs hover:bg-slate-700" data-testid={`audit-preset-${p.key}`}>{p.label}</button>
          ))}
        </div>
      </div>

      <div className="hidden print:block text-black text-sm">
        <p className="font-bold">Dossier d'audit SAAQ — {vLabel}</p>
        <p>Période : {from || 'début'} au {to || today()}</p>
      </div>

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-white font-semibold" data-testid="audit-total">{data.total} document(s)</span>
            {Object.entries(data.summary).map(([k, n]) => (
              <span key={k} className={`text-xs px-2 py-0.5 rounded-full ${TYPE_META[k]?.cls || ''}`} data-testid={`audit-count-${k}`}>{TYPE_META[k]?.label || k} : {n}</span>
            ))}
            <div className="ml-auto flex gap-2 print:hidden">
              <button onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm" data-testid="audit-print-btn">
                <Printer size={14} /> Imprimer la liste
              </button>
              <button onClick={downloadZip} disabled={downloading || !data.total} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="audit-download-btn">
                <Download size={14} /> {downloading ? 'Préparation…' : 'Télécharger le dossier (ZIP)'}
              </button>
            </div>
          </div>
          {data.total ? <AuditTable records={data.records} onVersions={setVersionsOf} /> : (
            <p className="text-slate-500 text-sm py-8 text-center" data-testid="audit-empty">Aucun document pour cette période.</p>
          )}
        </>
      )}

      <RetentionCard token={token} />
      {versionsOf && <RevisionsDialog record={versionsOf} token={token} onClose={() => setVersionsOf(null)} />}
    </div>
  );
}
